import ApiError from "@/api/ApiError";

interface ApiArgs {
  basePath: string;
}

const DEFAULT_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

class Api {
  basePath: string;

  constructor({ basePath }: ApiArgs) {
    this.basePath = basePath;
  }

  private buildUrl(path: string): string {
    if (path.startsWith("http")) {
      return path;
    }

    if (!path.startsWith("/")) {
      throw new Error(`[ApiClient] Path must start with "/": ${path}`);
    }

    return `${this.basePath}/api/v1${path}`;
  }

  private async request<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(this.buildUrl(url), {
      ...options,
      next: { revalidate: 60 },
      headers: { ...DEFAULT_HEADERS },
    });

    if (!response.ok) {
      const body = await response.json();
      throw new ApiError(response.status, body.message);
    }

    return response.json();
  }

  async get<T>(url: string, params?: object): Promise<T> {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(param => searchParams.append(`${key}[]`, param));
          } else {
            searchParams.append(key, String(value));
          }
        }
      });
    }

    const queryString = searchParams.size ? `?${searchParams}` : "";

    return this.request<T>(`${url}${queryString}`, { method: "GET" });
  }

  async post<T>(url: string, data?: object): Promise<T> {
    return this.request<T>(url, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(url: string, data?: object): Promise<T> {
    return this.request<T>(url, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(url: string, data?: object): Promise<T> {
    return this.request<T>(url, {
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export default Api;
